import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { X, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";

const PREVIEW_COLS = [
  { key: "tarih", label: "Tarih", src: "data", type: "date" },
  { key: "vade_tarihi", label: "Vade", src: "data", type: "date" },
  { key: "musteri", label: "Müşteri", src: "data" },
  { key: "urun", label: "Ürün", src: "data" },
  { key: "dosya_no", label: "Dosya No", src: "data" },
  { key: "icra_dairesi", label: "İcra Dairesi", src: "data" },
  { key: "alis_fiyati", label: "Alış", src: "data", type: "money" },
  { key: "satis_fiyati", label: "Satış", src: "data", type: "money" },
  { key: "icra_tutari", label: "İcra", src: "data", type: "money" },
  { key: "tahsilat", label: "Tahsilat", src: "data", type: "money" },
  { key: "odenen", label: "Ödenen", src: "data", type: "money" },
  { key: "asil_alacak", label: "Asıl Alacak", src: "data", type: "money" },
  { key: "kalan", label: "Kalan", src: "computed", type: "money" },
  { key: "net_risk", label: "Net Risk", src: "computed", type: "money" },
  { key: "durum", label: "Durum", src: "data" },
];

export default function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, total, valid_count, invalid_count}

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/records/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cari_sablon.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Şablon indirilemedi");
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Lütfen bir Excel dosyası seçin");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/records/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.total === 0) {
        toast.error("Dosyada veri satırı bulunamadı");
        setFile(null);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    const validRows = preview.rows.filter((r) => r.valid).map((r) => r.data);
    if (validRows.length === 0) {
      toast.error("İçe aktarılacak geçerli satır yok");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/records/import/confirm", { records: validRows });
      toast.success(`${data.inserted} kayıt içe aktarıldı`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setLoading(false);
    }
  };

  const cellVal = (r, c) => {
    const v = c.src === "computed" ? r.computed[c.key] : r.data[c.key];
    if (c.type === "date") return fmtDate(v);
    if (c.type === "money") return fmt(v);
    return v ?? "-";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div
        className={`bg-white border border-[#E7DECC] rounded-sm w-full my-10 ${preview ? "max-w-6xl" : "max-w-lg"}`}
        data-testid="import-dialog"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">
            {preview ? "İçe Aktarma Önizlemesi" : "Excel ile Toplu Ekle"}
          </h3>
          <button onClick={onClose} aria-label="Kapat" data-testid="import-close" className="text-[#8C8377] hover:text-[#26231E]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!preview ? (
          <div className="p-6">
            <div className="border border-[#E7DECC] bg-[#F3ECDD] rounded-sm p-4 mb-5">
              <div className="kicker mb-2">1. ŞABLONU İNDİRİN</div>
              <p className="text-sm text-[#8C8377] mb-3">
                Sütunlar: Müşteri, İşlem Tarihi, Vade Tarihi, Açıklama, Ürün Adı, Dosya No,
                İcra Dairesi, Alış/Satış Fiyatı, İcra Tutarı, Tahsilat,{" "}
                <strong>Faiz Tahsilatı</strong>, Ödenen, Asıl Alacak, Faiz Oranı, Durum.
                "Durum" sütununa <strong>İNFAZ</strong> yazarsanız dosya İnfaz olarak
                eklenir. Kalan ve risk otomatik hesaplanır.
              </p>
              <button
                onClick={downloadTemplate}
                data-testid="download-template-button"
                className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
              >
                <Download className="w-4 h-4 text-[#B8933F]" /> Şablonu İndir (.xlsx)
              </button>
            </div>

            <div className="kicker mb-2">2. DOSYAYI SEÇİN VE ÖNİZLEYİN</div>
            <label
              htmlFor="import-file"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E7DECC] rounded-sm py-8 cursor-pointer hover:border-[#B8933F] transition-colors duration-150"
            >
              <FileSpreadsheet className="w-8 h-8 text-[#2F6E4E]" />
              <span className="text-sm text-[#8C8377]">{file ? file.name : "Dosya seçmek için tıklayın (.xlsx)"}</span>
              <input
                id="import-file"
                type="file"
                accept=".xlsx"
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                data-testid="import-cancel-button"
                className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
              >
                İptal
              </button>
              <button
                onClick={runPreview}
                disabled={loading}
                data-testid="import-preview-button"
                className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60"
              >
                <Upload className="w-4 h-4" /> {loading ? "İşleniyor..." : "Önizle"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-1.5 text-sm" data-testid="preview-valid-count">
                <CheckCircle2 className="w-4 h-4 text-[#2F6E4E]" />
                <span className="font-semibold">{preview.valid_count}</span> geçerli satır
              </div>
              <div className="flex items-center gap-1.5 text-sm" data-testid="preview-invalid-count">
                <AlertCircle className="w-4 h-4 text-[#A63A3A]" />
                <span className="font-semibold">{preview.invalid_count}</span> hatalı satır
              </div>
              <span className="text-sm text-[#8C8377]">Toplam {preview.total}</span>
            </div>

            <div className="border border-[#E7DECC] rounded-sm overflow-auto max-h-[55vh]" data-testid="preview-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F3ECDD] z-10">
                  <tr className="border-b border-[#E7DECC]">
                    <th className="px-2 py-2 kicker text-left w-10">#</th>
                    {PREVIEW_COLS.map((c) => (
                      <th key={c.key} className={`px-2 py-2 kicker whitespace-nowrap ${c.type === "money" ? "text-right" : "text-left"}`}>
                        {c.label}
                      </th>
                    ))}
                    <th className="px-2 py-2 kicker text-left">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr
                      key={r.row_no}
                      data-testid={`preview-row-${r.row_no}`}
                      className={`border-b border-[#E7DECC] ${r.valid ? "" : "bg-[#A63A3A]/5"}`}
                    >
                      <td className="px-2 py-1.5 text-[#8C8377]">{r.row_no}</td>
                      {PREVIEW_COLS.map((c) => (
                        <td
                          key={c.key}
                          className={`px-2 py-1.5 whitespace-nowrap ${c.type === "money" ? "text-right tabular-nums" : ""} ${
                            c.key === "musteri" ? "font-medium" : ""
                          }`}
                        >
                          {cellVal(r, c)}
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        {r.valid ? (
                          <span className="inline-flex items-center gap-1 text-[#2F6E4E] text-xs font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Geçerli
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#A63A3A] text-xs font-semibold" title={r.errors.join(", ")}>
                            <AlertCircle className="w-3.5 h-3.5" /> {r.errors.join(", ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-[#8C8377] mt-3">
              Yalnızca geçerli satırlar kaydedilir. Hatalı satırları düzeltmek için Excel dosyasını güncelleyip tekrar yükleyin.
            </p>

            <div className="flex justify-between gap-3 mt-6">
              <button
                onClick={() => setPreview(null)}
                data-testid="preview-back-button"
                className="flex items-center gap-1.5 px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
              >
                <ArrowLeft className="w-4 h-4" /> Geri
              </button>
              <button
                onClick={confirmImport}
                disabled={loading || preview.valid_count === 0}
                data-testid="import-confirm-button"
                className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-50"
              >
                <Upload className="w-4 h-4" /> {loading ? "Kaydediliyor..." : `${preview.valid_count} Kaydı İçe Aktar`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
